import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import nexusApi from '../../services/nexusApi'
import {
  ArrowLeft, Building2, Users, BarChart2, LifeBuoy, Activity,
  CreditCard, Zap, AlertTriangle, CheckCircle, Copy, ExternalLink
} from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

const PLAN_PRICES: Record<string, number> = { trial: 0, standard: 299, professional: 599, enterprise: 1499 }

const TABS = ['Overview', 'Subscription', 'Users', 'Usage', 'Support', 'Activity']

export default function NexusTenantDetail() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<any>(null)
  const [usage, setUsage] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('Overview')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [impersonateResult, setImpersonateResult] = useState<any>(null)
  const [extendDays, setExtendDays] = useState(14)
  const [editPlan, setEditPlan] = useState(false)
  const [newPlan, setNewPlan] = useState('')
  const [copied, setCopied] = useState(false)
  const [showAddUser, setShowAddUser] = useState(false)
  const [addUserForm, setAddUserForm] = useState({ email: '', firstName: '', lastName: '', role: 'tenant_admin', password: '' })
  const [addUserLoading, setAddUserLoading] = useState(false)
  const [addUserResult, setAddUserResult] = useState<any>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([
      nexusApi.get(`/tenants/${id}`),
      nexusApi.get(`/tenants/${id}/usage`),
    ]).then(([r1, r2]) => {
      setData(r1.data)
      setUsage(r2.data)
      setNewPlan(r1.data.tenant.plan)
    }).catch(console.error).finally(() => setLoading(false))
  }, [id])

  async function doAction(action: string, body?: any) {
    setActionLoading(action)
    try {
      const { data: resp } = await nexusApi.post(`/tenants/${id}/${action}`, body || {})
      if (action === 'impersonate') {
        setImpersonateResult(resp)
      } else {
        alert(resp.message || 'Done')
        const r = await nexusApi.get(`/tenants/${id}`)
        setData(r.data)
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'Action failed')
    } finally {
      setActionLoading(null)
    }
  }

  async function updatePlan() {
    try {
      await nexusApi.patch(`/tenants/${id}`, { plan: newPlan })
      const r = await nexusApi.get(`/tenants/${id}`)
      setData(r.data)
      setEditPlan(false)
    } catch (e: any) {
      alert(e.response?.data?.error || 'Update failed')
    }
  }

  async function addUser() {
    if (!addUserForm.email) return
    setAddUserLoading(true)
    try {
      const { data: resp } = await nexusApi.post(`/tenants/${id}/users`, addUserForm)
      setAddUserResult(resp)
      const r = await nexusApi.get(`/tenants/${id}`)
      setData(r.data)
      setAddUserForm({ email: '', firstName: '', lastName: '', role: 'tenant_admin', password: '' })
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to create user')
    } finally {
      setAddUserLoading(false)
    }
  }

  function copyToken() {
    if (impersonateResult?.token) {
      navigator.clipboard.writeText(impersonateResult.token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (loading) return <div style={{ padding: '2rem', color: NX.textDim }}>Loading...</div>
  if (!data) return <div style={{ padding: '2rem', color: NX.red }}>Tenant not found</div>

  const { tenant, users, stats, supportHistory } = data
  const mrr = PLAN_PRICES[tenant.plan] || 0

  const inputStyle = {
    background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8,
    padding: '0.5rem 0.875rem', color: NX.text, fontSize: '0.875rem', outline: 'none',
  }

  return (
    <div style={{ padding: '2rem', background: NX.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.75rem' }}>
        <Link to="/nexus/tenants" style={{ color: NX.textDim, textDecoration: 'none', marginTop: 4 }}>
          <ArrowLeft size={18} />
        </Link>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: NX.text, fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{tenant.name}</h1>
          <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {tenant.industry || 'No industry'} · ABN: {tenant.abn || 'N/A'} · ID: {tenant.id}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
          <button onClick={() => doAction('impersonate')} disabled={actionLoading === 'impersonate'} style={{ background: 'rgba(0,102,255,0.15)', border: `1px solid ${NX.accent}`, color: NX.accent, borderRadius: 8, padding: '0.5rem 0.875rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}>
            <Zap size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Impersonate
          </button>
          <button onClick={() => doAction('suspend')} disabled={actionLoading === 'suspend'} style={{ background: 'rgba(239,68,68,0.1)', border: `1px solid ${NX.red}`, color: NX.red, borderRadius: 8, padding: '0.5rem 0.875rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}>
            Suspend
          </button>
          <button onClick={() => doAction('reactivate')} disabled={actionLoading === 'reactivate'} style={{ background: 'rgba(61,181,74,0.1)', border: `1px solid ${NX.green}`, color: NX.green, borderRadius: 8, padding: '0.5rem 0.875rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 }}>
            Reactivate
          </button>
        </div>
      </div>

      {/* Impersonate result */}
      {impersonateResult && (
        <div style={{ background: 'rgba(0,102,255,0.1)', border: `1px solid ${NX.accent}`, borderRadius: 10, padding: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ color: NX.text, fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertTriangle size={14} color={NX.amber} /> Impersonation Token (expires in 2h)
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <code style={{ color: NX.textDim, fontSize: '0.75rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{impersonateResult.token}</code>
            <button onClick={copyToken} style={{ background: NX.surface2, border: `1px solid ${NX.border}`, color: NX.text, borderRadius: 6, padding: '0.3rem 0.625rem', cursor: 'pointer', fontSize: '0.75rem' }}>
              {copied ? 'Copied!' : <Copy size={13} />}
            </button>
            <a href={`/impersonate?token=${impersonateResult.token}`} target="_blank" rel="noopener" style={{ background: NX.accent, color: '#fff', borderRadius: 6, padding: '0.3rem 0.625rem', fontSize: '0.75rem', textDecoration: 'none', fontWeight: 600 }}>
              Open as Tenant <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
            </a>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.875rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Users', value: users.length },
          { label: 'Incidents', value: stats.incidents },
          { label: 'Audits', value: stats.audits },
          { label: 'Actions', value: stats.corrective_actions },
          { label: 'Permits', value: stats.permits },
          { label: 'MRR', value: `$${mrr}` },
        ].map(s => (
          <div key={s.label} style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 10, padding: '0.875rem', textAlign: 'center' }}>
            <div style={{ color: NX.text, fontSize: '1.25rem', fontWeight: 700 }}>{s.value}</div>
            <div style={{ color: NX.textDim, fontSize: '0.75rem', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `1px solid ${NX.border}`, marginBottom: '1.5rem' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0.625rem 1rem',
            color: tab === t ? NX.accent : NX.textDim, fontSize: '0.875rem', fontWeight: tab === t ? 600 : 400,
            borderBottom: tab === t ? `2px solid ${NX.accent}` : '2px solid transparent',
          }}>{t}</button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'Overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
            <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Details</h3>
            {[
              ['Name', tenant.name], ['ABN', tenant.abn || 'N/A'], ['Industry', tenant.industry || 'N/A'],
              ['Plan', tenant.plan], ['Data Region', tenant.data_region || 'ap-southeast-2'],
              ['Created', new Date(tenant.created_at).toLocaleDateString('en-AU')],
              ['Plan Expires', tenant.plan_expires_at ? new Date(tenant.plan_expires_at).toLocaleDateString('en-AU') : 'N/A'],
            ].map(([k, v]) => (
              <div key={k as string} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', borderBottom: `1px solid ${NX.border}`, paddingBottom: '0.5rem' }}>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>{k}</span>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 500 }}>{v as string}</span>
              </div>
            ))}
          </div>
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
            <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {tenant.plan === 'trial' && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input type="number" value={extendDays} onChange={e => setExtendDays(parseInt(e.target.value))} style={{ ...inputStyle, width: 70 }} min={1} max={90} />
                  <button onClick={() => doAction('extend-trial', { days: extendDays })} disabled={actionLoading === 'extend-trial'} style={{ background: NX.amber, color: '#000', border: 'none', borderRadius: 8, padding: '0.5rem 0.875rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem' }}>
                    Extend Trial
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select value={newPlan} onChange={e => setNewPlan(e.target.value)} style={{ ...inputStyle }}>
                  {['trial', 'standard', 'professional', 'enterprise'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <button onClick={updatePlan} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 0.875rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem' }}>
                  Change Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'Users' && (
        <div>
          {/* Add User modal */}
          {showAddUser && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 14, padding: '1.75rem', width: 440, maxWidth: '95vw' }}>
                <h3 style={{ color: NX.text, fontSize: '1.125rem', fontWeight: 700, margin: '0 0 1.25rem' }}>Add User to {tenant.name}</h3>

                {addUserResult ? (
                  <div>
                    <div style={{ background: 'rgba(61,181,74,0.12)', border: `1px solid ${NX.green}`, borderRadius: 10, padding: '1rem', marginBottom: '1rem' }}>
                      <div style={{ color: NX.green, fontWeight: 700, marginBottom: '0.5rem' }}>
                        <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        User created successfully
                      </div>
                      <div style={{ color: NX.text, fontSize: '0.875rem' }}><strong>Email:</strong> {addUserResult.email}</div>
                      <div style={{ color: NX.text, fontSize: '0.875rem' }}><strong>Role:</strong> {addUserResult.role?.replace('_', ' ')}</div>
                      {addUserResult.tempPassword && (
                        <div style={{ color: NX.amber, fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          <strong>Temp password:</strong> <code style={{ background: NX.surface2, padding: '2px 6px', borderRadius: 4 }}>{addUserResult.tempPassword}</code>
                        </div>
                      )}
                      <div style={{ marginTop: '0.625rem', fontSize: '0.8125rem', color: addUserResult.emailSent ? NX.green : NX.amber }}>
                        {addUserResult.emailSent ? '✉ Welcome email sent to user' : '⚠ Welcome email could not be sent — share credentials manually'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button onClick={() => { setAddUserResult(null); setShowAddUser(false) }} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>Done</button>
                      <button onClick={() => setAddUserResult(null)} style={{ background: NX.surface2, color: NX.textDim, border: `1px solid ${NX.border}`, borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>Add Another</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                    {[
                      { label: 'Email *', key: 'email', type: 'email', placeholder: 'user@company.com' },
                      { label: 'First Name', key: 'firstName', type: 'text', placeholder: 'First' },
                      { label: 'Last Name', key: 'lastName', type: 'text', placeholder: 'Last' },
                      { label: 'Password (leave blank for default)', key: 'password', type: 'password', placeholder: 'Welcome2026!' },
                    ].map(f => (
                      <div key={f.key}>
                        <label style={{ display: 'block', color: NX.textDim, fontSize: '0.8125rem', marginBottom: '0.3rem' }}>{f.label}</label>
                        <input
                          type={f.type} placeholder={f.placeholder}
                          value={(addUserForm as any)[f.key]}
                          onChange={e => setAddUserForm(p => ({ ...p, [f.key]: e.target.value }))}
                          style={{ ...inputStyle, width: '100%' }}
                        />
                      </div>
                    ))}
                    <div>
                      <label style={{ display: 'block', color: NX.textDim, fontSize: '0.8125rem', marginBottom: '0.3rem' }}>Role</label>
                      <select value={addUserForm.role} onChange={e => setAddUserForm(p => ({ ...p, role: e.target.value }))} style={{ ...inputStyle, width: '100%' }}>
                        <option value="tenant_admin">Tenant Admin</option>
                        <option value="ops_safety">Ops Safety</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="worker">Worker</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
                      <button onClick={() => setShowAddUser(false)} style={{ background: 'none', border: `1px solid ${NX.border}`, color: NX.textDim, borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>Cancel</button>
                      <button onClick={addUser} disabled={addUserLoading || !addUserForm.email} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.625rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: addUserLoading || !addUserForm.email ? 0.6 : 1 }}>
                        {addUserLoading ? 'Creating...' : 'Create User'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users table with Add User button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
            <span style={{ color: NX.textDim, fontSize: '0.875rem' }}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
            <button onClick={() => { setShowAddUser(true); setAddUserResult(null) }} style={{ background: NX.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 0.875rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8125rem' }}>
              + Add User
            </button>
          </div>

          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {users.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: NX.textDim }}>No users yet — add the first one above</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                    {['Name', 'Email', 'Role', 'Last Login', 'Status', 'Flags'].map(h => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u: any, i: number) => (
                    <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? `1px solid ${NX.border}` : 'none' }}>
                      <td style={{ padding: '0.75rem 1rem', color: NX.text, fontSize: '0.875rem' }}>{u.first_name} {u.last_name}</td>
                      <td style={{ padding: '0.75rem 1rem', color: NX.textDim, fontSize: '0.875rem' }}>{u.email}</td>
                      <td style={{ padding: '0.75rem 1rem', color: NX.textDim, fontSize: '0.8125rem', textTransform: 'capitalize' }}>{u.role?.replace('_', ' ')}</td>
                      <td style={{ padding: '0.75rem 1rem', color: NX.textDim, fontSize: '0.8125rem' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString('en-AU') : 'Never'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ color: u.active ? NX.green : NX.red, fontSize: '0.8125rem' }}>{u.active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                          <span title={u.welcome_email_sent ? 'Welcome email sent' : 'Email not sent'} style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 5px', borderRadius: 3, background: u.welcome_email_sent ? 'rgba(61,181,74,0.15)' : 'rgba(239,68,68,0.12)', color: u.welcome_email_sent ? NX.green : NX.red }}>
                            {u.welcome_email_sent ? '✉ Sent' : '✉ Unsent'}
                          </span>
                          {u.first_login_at ? (
                            <span title={`First login: ${new Date(u.first_login_at).toLocaleDateString('en-AU')}`} style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 5px', borderRadius: 3, background: 'rgba(61,181,74,0.15)', color: NX.green }}>1st ✓</span>
                          ) : (
                            <span title="Never logged in" style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 5px', borderRadius: 3, background: 'rgba(245,146,30,0.15)', color: NX.amber }}>1st –</span>
                          )}
                          {u.password_changed_at ? (
                            <span title={`Password changed: ${new Date(u.password_changed_at).toLocaleDateString('en-AU')}`} style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 5px', borderRadius: 3, background: 'rgba(61,181,74,0.15)', color: NX.green }}>PW ✓</span>
                          ) : (
                            <span title={u.must_change_password ? 'Temp password — must change' : 'Password not changed'} style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 5px', borderRadius: 3, background: u.must_change_password ? 'rgba(239,68,68,0.12)' : 'rgba(139,148,158,0.12)', color: u.must_change_password ? NX.red : NX.textDim }}>PW {u.must_change_password ? '!' : '–'}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'Usage' && usage && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {Object.entries(usage.usage).map(([k, v]) => (
            <div key={k} style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 10, padding: '1rem' }}>
              <div style={{ color: NX.text, fontSize: '1.5rem', fontWeight: 700 }}>{v as number}</div>
              <div style={{ color: NX.textDim, fontSize: '0.8125rem', textTransform: 'capitalize', marginTop: 4 }}>{k.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'Support' && (
        <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, overflow: 'hidden' }}>
          {supportHistory.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: NX.textDim }}>No support tickets</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                  {['Subject', 'Priority', 'Status', 'Assigned', 'Created'].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {supportHistory.map((t: any, i: number) => (
                  <tr key={t.id} style={{ borderBottom: i < supportHistory.length - 1 ? `1px solid ${NX.border}` : 'none' }}>
                    <td style={{ padding: '0.75rem 1rem', color: NX.text, fontSize: '0.875rem' }}>{t.subject}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{ color: t.priority === 'p1' ? NX.red : t.priority === 'p2' ? NX.amber : NX.textDim, fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase' }}>{t.priority}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: NX.textDim, fontSize: '0.8125rem', textTransform: 'capitalize' }}>{t.status.replace('_', ' ')}</td>
                    <td style={{ padding: '0.75rem 1rem', color: NX.textDim, fontSize: '0.8125rem' }}>{t.assigned_name || 'Unassigned'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: NX.textDim, fontSize: '0.8125rem' }}>{new Date(t.created_at).toLocaleDateString('en-AU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'Subscription' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
            <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Billing Info</h3>
            {[
              ['Current Plan', tenant.plan],
              ['MRR', `$${mrr}`],
              ['ARR', `$${mrr * 12}`],
              ['Stripe Customer', tenant.stripe_customer_id || 'Not linked'],
              ['Stripe Subscription', tenant.stripe_subscription_id || 'Not linked'],
              ['Plan Expires', tenant.plan_expires_at ? new Date(tenant.plan_expires_at).toLocaleDateString('en-AU') : 'N/A'],
            ].map(([k, v]) => (
              <div key={k as string} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', borderBottom: `1px solid ${NX.border}`, paddingBottom: '0.5rem' }}>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>{k}</span>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'capitalize' }}>{v as string}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'Activity' && (
        <div style={{ color: NX.textDim, fontSize: '0.875rem', padding: '1rem', background: NX.surface, borderRadius: 12, border: `1px solid ${NX.border}` }}>
          Activity log is available via the tenant audit trail. Use the impersonation feature to view detailed tenant activity.
        </div>
      )}
    </div>
  )
}
